import type { NextPageContext } from 'next';
import { LanguageCode } from '$/store/languageStore/types';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getOfficialHomeLanguage } from '@/utils/officialHome';
import { OFFICIAL_HOME_TRANSLATION_KEYS } from '@/components/overseaHome/types';
import type { OfficialHomeMessages, OfficialHomePageProps } from '@/components/overseaHome/types';
import { getOverseaHomeServerData } from './overseaHomeServerData';

/** 两个官网入口共用请求参数和数据准备；SSR 语言资源按请求传递，不修改全局 Store。 */
export async function getOfficialHomePageData(context: NextPageContext): Promise<OfficialHomePageProps> {
  const request = context.req as (typeof context.req & { countryCode?: string; }) | undefined;
  const isBrowser = typeof window !== 'undefined';
  // SSR 只使用当前请求数据；客户端导航没有 req 时才读取已初始化的地区 Store。
  const countryCode = (request?.countryCode
    || (isBrowser ? useOfficialStore.getState().currentCountryCode : 'CN')).trim().toUpperCase() || 'CN';
  const cookie = request?.headers.cookie || (isBrowser ? document.cookie : '');
  const userAgent = request?.headers['user-agent'] || (isBrowser ? navigator.userAgent : '');
  const languageCode = getOfficialHomeLanguage(countryCode);
  // 接口数据和本地文案共用地区默认语言，避免历史语言 Cookie 覆盖官网版本。
  const [{ activityData, menus }, locale] = await Promise.all([
    getOverseaHomeServerData({ countryCode, cookie, languageCode }),
    languageCode === LanguageCode.zhHans
      ? import('$/store/languageStore/smartcatTranslation/zh.json')
      : import('$/store/languageStore/smartcatTranslation/en.json'),
  ]);
  // 仅将首屏使用的已有 key 随 pageProps 下发，避免序列化整份语言包。
  const messages = Object.fromEntries(
    Object.entries(OFFICIAL_HOME_TRANSLATION_KEYS).map(([name, key]) => [name, locale.default[key]]),
  ) as OfficialHomeMessages;

  return {
    countryCode,
    languageCode,
    messages,
    userAgent,
    // 导航完整保留接口分组、标题和顺序，避免分类改名或新增后被前端过滤。
    menus: menus.list,
    activityList: activityData?.list ?? [],
  };
}
